import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn, fetchAuthSession, signInWithRedirect, confirmSignIn, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../App';
import { mapCognitoError } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import Logo from '../components/Logo';

// T18: Split-panel auth layout.
// - On desktop, a 40/60 split: branded teal panel on the left, form on the right.
// - On mobile, collapse to a single column (form-only, no left panel) per the
//   useWindowWidth isMobile check.
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  splitContainer: (isMobile) => ({
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    width: '100%',
    minHeight: '100vh',
  }),
  brandPanel: {
    flex: '0 0 40%',
    backgroundColor: '#01696F',
    color: '#ffffff',
    padding: '3rem 2.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    overflow: 'hidden',
  },
  brandLogoWrap: {
    marginBottom: '2rem',
    filter: 'brightness(0) invert(1)', // render dark logo as white
  },
  brandHeadline: {
    fontSize: '2rem',
    fontWeight: '700',
    lineHeight: 1.2,
    marginBottom: '1rem',
    color: '#ffffff',
  },
  brandTagline: {
    fontSize: '1rem',
    fontWeight: '400',
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.85)',
    maxWidth: '380px',
  },
  formPanel: {
    flex: '1 1 60%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backgroundColor: '#f8fafc',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
  },
  logo: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#01696F',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  subtitle: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '2rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1e293b',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  btn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#01696F',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
    minHeight: '44px',
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  footer: {
    textAlign: 'center',
    marginTop: '1.5rem',
    fontSize: '0.875rem',
    color: '#64748b',
  },
  footerLink: {
    color: '#01696F',
    fontWeight: '500',
    textDecoration: 'none',
  },
  successBanner: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#16a34a',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  forgotLink: {
    display: 'block',
    textAlign: 'right',
    marginTop: '0.5rem',
    marginBottom: '0.25rem',
    fontSize: '0.8rem',
    color: '#01696F',
    fontWeight: '500',
    textDecoration: 'none',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    margin: '1.5rem 0',
    color: '#94a3b8',
    fontSize: '0.8rem',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0',
  },
  googleBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    minHeight: '44px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  googleIcon: {
    width: '18px',
    height: '18px',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  toggleBtn: {
    position: 'absolute',
    right: '0',
    top: '0',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    color: '#94a3b8',
  },
  // T16: Stay-signed-in checkbox row — small font, sits bottom-left below the
  // password field, above the "Forgot your password?" link.
  staySignedInRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#64748b',
    cursor: 'pointer',
    userSelect: 'none',
  },
  staySignedInCheckbox: {
    width: '14px',
    height: '14px',
    accentColor: '#01696F',
    cursor: 'pointer',
  },
};

// T16: localStorage key for "Stay signed in" preference. Cognito's refresh
// token already lives 30 days by default, so the practical effect of this
// flag is that we don't actively clear tokens on browser close. The current
// auth wiring already persists to localStorage, so this is effectively an
// explicit opt-in marker we can read in App's logout/cleanup paths later.
const STAY_SIGNED_IN_KEY = 'bragsheet_stay_signed_in';

const EyeIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resendStatus, setResendStatus] = useState(''); // '', 'sending', 'sent', 'error'
  const [resendError, setResendError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');
  const isGoogleConfigured = Boolean(process.env.REACT_APP_COGNITO_DOMAIN);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  // T16: "Stay signed in" — default unchecked. Persisted to localStorage when
  // the user actually submits so a re-visit to /login pre-fills the previous
  // choice (purely a UX nicety; Cognito's 30-day refresh token is the real
  // session length).
  const [staySignedIn, setStaySignedIn] = useState(() => {
    try {
      return window.localStorage.getItem(STAY_SIGNED_IN_KEY) === 'true';
    } catch (_) {
      return false;
    }
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useWindowWidth();

  const flashMessage = location.state?.flash || '';

  // Adopt an existing Cognito session into our React auth context. Returns
  // true if a valid session was found and the user was navigated to /dashboard.
  // Used to recover from the Amplify v6 quirk where signIn() throws
  // UserAlreadyAuthenticatedException when a session already exists (which
  // happens after a successful confirmSignUp from the registration flow).
  const adoptCognitoSession = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();
      if (!idToken) return false;
      const payload = session.tokens.idToken.payload || {};
      login(idToken, {
        id: payload.sub,
        email: payload.email,
        name: payload.name || payload['cognito:username'] || '',
      });
      navigate('/dashboard');
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setMfaRequired(false);
    setLoading(true);
    // T16: Persist the "Stay signed in" preference so the box reflects the
    // user's last choice on subsequent visits. Storage writes are fire-and-
    // forget — a failure must not block sign-in.
    try {
      window.localStorage.setItem(STAY_SIGNED_IN_KEY, staySignedIn ? 'true' : 'false');
    } catch (_) {
      // Storage disabled (private mode); ignore.
    }
    try {
      // Defensive: if a Cognito session already exists for this browser
      // (e.g. user just verified their email and confirmSignUp established
      // a session as a side effect), adopt it instead of calling signIn()
      // which would throw UserAlreadyAuthenticatedException.
      if (await adoptCognitoSession()) return;

      const signInResult = await signIn({ username: email, password });

      // Check if new password is required (admin-created users)
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNewPasswordRequired(true);
        setLoading(false);
        return;
      }

      // Check if MFA is required
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMfaRequired(true);
        setLoading(false);
        return;
      }

      const session = await fetchAuthSession();
      const idToken = session.tokens && session.tokens.idToken ? session.tokens.idToken.toString() : null;
      const payload = session.tokens && session.tokens.idToken ? session.tokens.idToken.payload : {};
      const userPayload = {
        id: payload.sub,
        email: payload.email,
        name: payload.name || payload['cognito:username'] || '',
      };
      login(idToken, userPayload);
      navigate('/dashboard');
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        setUnverified(true);
      } else if (err.name === 'UserAlreadyAuthenticatedException') {
        // Race: session was established between our pre-check and signIn().
        // Adopt the existing session instead of failing.
        if (await adoptCognitoSession()) return;
        setError('You are already signed in. Refresh the page to continue.');
      } else {
        setError(mapCognitoError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaLoading(true);
    try {
      await confirmSignIn({ challengeResponse: mfaCode });
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString() || null;
      const payload = session.tokens?.idToken?.payload || {};
      const userPayload = {
        id: payload.sub,
        email: payload.email,
        name: payload.name || payload['cognito:username'] || '',
      };
      login(idToken, userPayload);
      navigate('/dashboard');
    } catch (err) {
      setError(mapCognitoError(err));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleError('');
    if (!isGoogleConfigured) {
      setGoogleError('Google sign-in is not configured yet. Please use email and password.');
      return;
    }
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setGoogleError(err.message || 'Google sign-in failed. Please try again.');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.splitContainer(isMobile)}>
        {/* T18: Brand panel — hidden on mobile to keep the form viewport-first. */}
        {!isMobile && (
          <div style={styles.brandPanel}>
            <div style={styles.brandLogoWrap}>
              <Link to="/" style={{ textDecoration: 'none' }}><Logo size="large" /></Link>
            </div>
            <h1 style={styles.brandHeadline}>Your college journey starts here.</h1>
            <p style={styles.brandTagline}>
              Capture wins, build your story, and walk into application season ready.
            </p>
          </div>
        )}
        <div style={styles.formPanel}>
          <div style={{ ...styles.card, padding: isMobile ? '1.5rem 1.25rem' : '2.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Link to="/" style={{ textDecoration: 'none' }}><Logo size="medium" /></Link></div>
            <div style={styles.subtitle}>Sign in to your college journey</div>

        {flashMessage && <div style={styles.successBanner}>{flashMessage}</div>}

        {unverified && (
          <div style={styles.error}>
            <div>Please verify your email first. Check your inbox for the verification code.</div>
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={async () => {
                  setResendStatus('sending');
                  setResendError('');
                  try {
                    await resendSignUpCode({ username: email });
                    setResendStatus('sent');
                  } catch (err) {
                    setResendStatus('error');
                    setResendError(err.message || 'Failed to resend verification code.');
                  }
                }}
                disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#01696F',
                  fontWeight: '600',
                  textDecoration: 'underline',
                  cursor: resendStatus === 'sending' || resendStatus === 'sent' ? 'default' : 'pointer',
                  fontSize: '0.875rem',
                  opacity: resendStatus === 'sending' ? 0.6 : 1,
                }}
              >
                {resendStatus === 'sending' ? 'Sending...' : resendStatus === 'sent' ? 'Code sent!' : 'Resend verification code'}
              </button>
              <Link
                to={`/verify-email?email=${encodeURIComponent(email)}`}
                style={{ color: '#01696F', fontWeight: '600', textDecoration: 'underline', fontSize: '0.875rem' }}
              >
                Enter code &rarr;
              </Link>
            </div>
            {resendStatus === 'sent' && (
              <div style={{ marginTop: '0.5rem', color: '#16a34a', fontSize: '0.8rem' }}>
                A new verification code has been sent to your email.
              </div>
            )}
            {resendStatus === 'error' && resendError && (
              <div style={{ marginTop: '0.5rem', color: '#ef4444', fontSize: '0.8rem' }}>
                {resendError}
              </div>
            )}
          </div>
        )}

        {mfaRequired && (
          <form onSubmit={handleMfaSubmit}>
            <div style={{ marginBottom: '1rem', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', color: '#01696F', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
              Enter the 6-digit code from your authenticator app.
            </div>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.formGroup}>
              <label style={styles.label}>Verification Code</label>
              <input
                style={styles.input}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                autoFocus
                autoComplete="one-time-code"
                onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(mfaLoading ? styles.btnDisabled : {}) }}
              disabled={mfaLoading}
            >
              {mfaLoading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              type="button"
              style={{ ...styles.btn, backgroundColor: 'transparent', color: '#64748b', border: '1px solid #e2e8f0', marginTop: '0.75rem', width: '100%' }}
              onClick={async () => {
                try { const { signOut } = await import('aws-amplify/auth'); await signOut(); } catch (_) {}
                setMfaRequired(false);
                setMfaCode('');
                setError('');
              }}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {newPasswordRequired && (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setError('');
            setLoading(true);
            try {
              await confirmSignIn({ challengeResponse: newPassword });
              const session = await fetchAuthSession();
              const idToken = session.tokens?.idToken?.toString() || null;
              const payload = session.tokens?.idToken?.payload || {};
              login(idToken, { id: payload.sub, email: payload.email, name: payload.name || payload['cognito:username'] || '' });
              navigate('/dashboard');
            } catch (err) {
              setError(mapCognitoError(err));
            } finally {
              setLoading(false);
            }
          }}>
            <div style={{ marginBottom: '1rem', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', color: '#01696F', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}>
              Your account requires a new password. Please create one below.
            </div>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  style={{ ...styles.input, paddingRight: '2.75rem' }}
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Create a new password (min 8 characters)"
                  required
                  minLength={8}
                  autoFocus
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
                <button
                  type="button"
                  style={styles.toggleBtn}
                  onClick={() => setShowNewPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={showNewPassword} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? 'Setting password...' : 'Set Password & Sign In'}
            </button>
          </form>
        )}

        {!mfaRequired && !newPasswordRequired && (
          <>
            {/* Error stays focused — just the message. The resend link lives in
                the form footer (always discoverable, never coupled to an error)
                so it doesn't imply that resending a code might fix a wrong password. */}
            {error && (
              <div style={styles.error}>
                <div>{error}</div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordWrapper}>
                  <input
                    style={{ ...styles.input, paddingRight: '2.75rem' }}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    onFocus={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.15)'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    style={styles.toggleBtn}
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {/* T16: "Stay signed in" sits bottom-left of the password field,
                    sized small (0.85rem) so it reads as a quiet preference toggle
                    rather than a primary action. */}
                <label style={styles.staySignedInRow}>
                  <input
                    type="checkbox"
                    checked={staySignedIn}
                    onChange={e => setStaySignedIn(e.target.checked)}
                    style={styles.staySignedInCheckbox}
                  />
                  <span>Stay signed in</span>
                </label>
                <Link to="/forgot-password" style={styles.forgotLink}>Forgot your password?</Link>
              </div>
              <button
                type="submit"
                style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span>or</span>
              <span style={styles.dividerLine} />
            </div>

            {googleError && <div style={styles.error}>{googleError}</div>}

            <button
              type="button"
              style={styles.googleBtn}
              onClick={handleGoogleSignIn}
              onMouseEnter={(e) => { e.target.style.borderColor = '#01696F'; e.target.style.boxShadow = '0 0 0 3px rgba(1, 105, 111, 0.1)'; }}
              onMouseLeave={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            >
              <svg style={styles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>

            <div style={styles.footer}>
              Don't have an account?{' '}
              <Link to="/register" style={styles.footerLink}>Create one</Link>
            </div>

            {/* Resend verification code lives only inside the `unverified`
                sub-step above. Showing it on the main email/password form
                implies the user is mid-verification, which is confusing for
                users with already-verified accounts trying to sign in. */}
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
